// @typescript-eslint/no-explicit-any
"use client"

import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay, EffectFade, Pagination } from "swiper/modules"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import "swiper/css/effect-fade"
import { useEffect, useRef, useState } from "react"
import { Button } from "@heroui/react"
import { SingleCarouselCardProps } from "@/util/enum"
import { MdNavigateNext } from "react-icons/md"
import { GrFormPrevious } from "react-icons/gr"
import { getBreakpoints } from "@/util/SwiperCard"

// Use this SwiperWithArrow when want to show the navigation arrow at the top in mobile screen

export default function SwiperWithArrow({ pageName, carousel_title, crouselData, renderItem, showPagination = false, slideDelayTime = 4000, pauseAutoplayOnSlideInteraction = false }: SingleCarouselCardProps) {

    const [isBeginning, setIsBeginning] = useState(true)
    const [isEnd, setIsEnd] = useState(false)
    const [hasEnoughContent, setHasEnoughContent] = useState(false)
    const swiperRef = useRef<SwiperType | null>(null)

    // Function to calculate if navigation should be shown based on viewport and content
    const calculateHasEnoughContent = (swiper: SwiperType) => {
        const totalSlides = swiper.slides.length
        const slidesPerView = swiper.params.slidesPerView
        return totalSlides > (typeof slidesPerView === "number" ? slidesPerView : 1)
    }

    // Recalculate when crouselData changes (fixes pagination not showing when data loads async)
    useEffect(() => {
        if (swiperRef.current) {
            setHasEnoughContent(calculateHasEnoughContent(swiperRef.current))
        }
    }, [crouselData])

    const handlePrev = () => {
        if (!swiperRef.current) return;
        if (isBeginning) {
            swiperRef.current.slideTo(swiperRef.current.slides.length - 1);
        } else {
            swiperRef.current.slidePrev();
        }
    };

    const handleNext = () => {
        if (!swiperRef.current) return;
        swiperRef.current.slideNext();
    };


    return (
        <div className={`relative z-8!`}>

            <div className="absolute -top-10 inset-0 flex flex-row justify-end md:justify-between items-start md:items-center gap-2 md:gap-0 pt-2 md:pt-0 pointer-events-none">
                {carousel_title ? (
                    <h3 className="crousel_head_text absolute top-5 left-1/2 -translate-x-1/2">
                        {carousel_title}
                    </h3>
                ) : null}

                {(hasEnoughContent && !showPagination) ? (
                    <>
                        <Button
                            onClick={handlePrev}
                            aria-label="Previous"
                            endContent={<GrFormPrevious className="text-lightBrown text-xl lg:text-2xl" />}
                            className="flex btn_global icon_button_prev rounded_btn border-1 lg:border-1.5 border-lightBrown pointer-events-auto ml-0 lg:-ml-14"
                        />
                        <Button
                            onClick={handleNext}
                            aria-label="Next"
                            endContent={<MdNavigateNext className="text-lightBrown text-xl lg:text-2xl" />}
                            className="flex btn_global icon_button_forward rounded_btn border-1 lg:border-1.5 border-lightBrown pointer-events-auto mr-0 lg:-mr-14"
                        />
                    </>
                ) : <></>}
            </div>
            <div className={`swiper_slider_container overflow-hidden mt-8`}>
                <Swiper
                    autoHeight={true}
                    pagination={(showPagination && hasEnoughContent) ? { clickable: true, dynamicBullets: true } : false}
                    modules={[Pagination, Autoplay, EffectFade]}
                    allowTouchMove={false}
                    loop={true}
                    autoplay={{
                        delay: slideDelayTime,
                        disableOnInteraction: true,
                        pauseOnMouseEnter: true,
                    }}
                    slidesPerView={1}
                    spaceBetween={50}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper
                        setIsBeginning(swiper.isBeginning)
                        setIsEnd(swiper.isEnd)
                        setHasEnoughContent(calculateHasEnoughContent(swiper))
                    }}
                    onSlideChange={(swiper) => {
                        setIsBeginning(swiper.isBeginning)
                        setIsEnd(swiper.isEnd)
                    }}
                    onBreakpoint={(swiper) => {
                        setHasEnoughContent(calculateHasEnoughContent(swiper))
                    }}
                    breakpoints={getBreakpoints(pageName, 50)}
                    className={`my-swiper ${showPagination ? "swiper-show-pagination" : ""}`}
                >
                    {crouselData?.map((item: any, index: number) => (
                        <SwiperSlide key={index} className="px-0! pb-6 md:pb-8">
                            {pauseAutoplayOnSlideInteraction ? (
                                <div
                                    className="contents"
                                    onTouchStart={() => swiperRef.current?.autoplay?.stop()}
                                    onTouchEnd={() => swiperRef.current?.autoplay?.start()}
                                    onTouchCancel={() => swiperRef.current?.autoplay?.start()}
                                    onPointerDown={() => swiperRef.current?.autoplay?.stop()}
                                    onPointerUp={() => swiperRef.current?.autoplay?.start()}
                                    onPointerLeave={() => swiperRef.current?.autoplay?.start()}
                                >
                                    {renderItem(item, index)}
                                </div>
                            ) : (
                                renderItem(item, index)
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>

            </div>
        </div>
    )
}

